import { useEffect, useMemo, useRef, useState } from "react";
import {
  Check,
  Clock,
  GripHorizontal,
  Search,
  Swords,
  UserMinus,
  UserPlus,
  UserRound,
  Users,
  X,
} from "lucide-react";
import {
  friendsStore,
  shortAddr,
  useFriends,
  type DiscoverableUser,
} from "../store/friendsStore";
import { usersApi, avatarUrl } from "../auth/api";
import Avatar from "./Avatar";

interface FriendsPanelProps {
  walletAddress: string | null;
  handle: string | null;
  /** Server-side auth token from useAuth — used by the find-people tab
   *  to hit the real users directory. When null we degrade to the local
   *  KNOWN_USERS mock list. */
  apiToken: string | null;
  onClose: () => void;
  /** Open the profile page focused on this user. The panel closes
   *  itself after dispatching so the user lands on a clean profile view. */
  onViewProfile?: (address: string, handle: string) => void;
  /** Open the challenge modal to invite this friend to a custom match. */
  onChallenge?: (target: { address: string; handle: string; avatarVersion: number }) => void;
}

const PANEL_WIDTH = 380;

function defaultPosition() {
  if (typeof window === "undefined") return { x: 0, y: 80 };
  return { x: Math.max(16, window.innerWidth - PANEL_WIDTH - 24), y: 80 };
}

export default function FriendsPanel({
  walletAddress,
  handle,
  apiToken,
  onClose,
  onViewProfile,
  onChallenge,
}: FriendsPanelProps) {
  const [pos, setPos] = useState(defaultPosition);
  const [dragging, setDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const [tab, setTab] = useState<"friends" | "find">("friends");
  const [query, setQuery] = useState("");
  const friends = useFriends();

  // Esc closes
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Drag
  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => {
      const newX = e.clientX - dragOffset.current.x;
      const newY = e.clientY - dragOffset.current.y;
      setPos({
        x: Math.max(-PANEL_WIDTH + 80, Math.min(newX, window.innerWidth - 80)),
        y: Math.max(0, Math.min(newY, window.innerHeight - 60)),
      });
    };
    const onUp = () => setDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [dragging]);
  const startDrag = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest("[data-no-drag]")) return;
    dragOffset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    setDragging(true);
    e.preventDefault();
  };

  const friendSet = useMemo(
    () => new Set(friends.friends.map((f) => f.address.toLowerCase())),
    [friends.friends],
  );
  const outgoingSet = useMemo(
    () => new Set(friends.outgoing.map((r) => r.toAddress.toLowerCase())),
    [friends.outgoing],
  );

  // Find-people search: hits the real users directory (debounced 200ms).
  // The backend already excludes the caller and returns each player's
  // current handle/online status.
  const [searchResults, setSearchResults] = useState<DiscoverableUser[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  useEffect(() => {
    if (tab !== "find") return;
    if (!apiToken) {
      setSearchResults([]);
      setSearching(false);
      setSearchError(null);
      return;
    }
    const q = query.trim();
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setSearching(true);
      setSearchError(null);
      try {
        const res = await usersApi.search(apiToken, q);
        if (cancelled) return;
        setSearchResults(
          res.users.map((u) => ({
            address: u.wallet,
            handle: u.handle ?? shortAddr(u.wallet),
            online: u.online,
            avatarVersion: u.avatarVersion ?? 0,
          })),
        );
      } catch (err) {
        if (cancelled) return;
        setSearchResults([]);
        setSearchError(err instanceof Error ? err.message : "Search service unreachable");
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 200);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [tab, query, apiToken]);

  const handleAdd = (u: DiscoverableUser) => {
    if (!walletAddress) return;
    void friendsStore.sendRequest(u);
  };

  return (
    <div
      role="dialog"
      aria-label="Friends"
      className="fixed z-50 bg-[#111] border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
      style={{
        left: pos.x,
        top: pos.y,
        width: PANEL_WIDTH,
        userSelect: dragging ? "none" : "auto",
      }}
    >
      <div className="h-1 bg-gradient-to-r from-[#FF6B00] to-[#F54D12]" />

      <div
        onMouseDown={startDrag}
        className="px-5 py-3.5 flex items-center justify-between border-b border-white/5"
        style={{ cursor: dragging ? "grabbing" : "grab" }}
      >
        <div className="flex items-center gap-2.5">
          <GripHorizontal className="w-4 h-4 text-white/30" aria-hidden="true" />
          <Users className="w-4 h-4 text-[#FF6B00]" />
          <span className="text-[13px] text-white font-semibold tracking-tight">Friends</span>
          <span className="font-mono text-[10px] tracking-[0.14em] uppercase text-white/40 ml-1">
            {friends.friends.length}
          </span>
        </div>
        <button
          data-no-drag
          onClick={onClose}
          aria-label="Close"
          className="w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/50 hover:text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex bg-white/3 border-b border-white/5">
        <TabButton active={tab === "friends"} onClick={() => setTab("friends")}>
          Friends
          <span className="ml-2 font-mono text-[10px] tabular-nums opacity-60">
            {friends.friends.length}
          </span>
        </TabButton>
        <TabButton active={tab === "find"} onClick={() => setTab("find")}>
          Find people
          {friends.outgoing.length > 0 && (
            <span className="ml-2 font-mono text-[10px] tabular-nums text-[#FF6B00]">
              {friends.outgoing.length} pending
            </span>
          )}
        </TabButton>
      </div>

      {/* Search bar — visible in both tabs (filters friends or users) */}
      <div className="px-3.5 py-2.5 border-b border-white/5">
        <div className="flex items-center gap-2 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 focus-within:border-[#FF6B00]/40">
          <Search className="w-4 h-4 text-white/30" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={tab === "friends" ? "Search your friends" : "Search by handle or address"}
            className="flex-1 bg-transparent outline-none text-[13px] text-white placeholder-white/30"
          />
        </div>
      </div>

      <div className="max-h-[460px] overflow-auto">
        {tab === "friends" ? (
          <FriendsList query={query} onViewProfile={onViewProfile} onChallenge={onChallenge} />
        ) : (
          <FindList
            query={query}
            results={searchResults}
            searching={searching}
            error={searchError}
            friendSet={friendSet}
            outgoingSet={outgoingSet}
            disabled={!walletAddress}
            onAdd={handleAdd}
            onViewProfile={onViewProfile}
          />
        )}
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 px-4 py-2.5 font-mono text-[11px] tracking-[0.12em] uppercase font-bold transition-colors ${
        active
          ? "bg-white/5 text-white border-b-2 border-[#FF6B00] -mb-px"
          : "text-white/40 hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

function FriendsList({
  query,
  onViewProfile,
  onChallenge,
}: {
  query: string;
  onViewProfile?: (address: string, handle: string) => void;
  onChallenge?: (target: { address: string; handle: string; avatarVersion: number }) => void;
}) {
  const friends = useFriends();
  const q = query.trim().toLowerCase();
  const filtered = friends.friends.filter(
    (f) => !q || f.handle.toLowerCase().includes(q) || f.address.toLowerCase().includes(q),
  );

  if (friends.friends.length === 0) {
    return (
      <div className="py-12 px-6 flex flex-col items-center text-center">
        <Users className="w-10 h-10 text-white/20 mb-3" strokeWidth={1.5} />
        <div className="text-[14px] text-white/60 mb-1">No friends yet.</div>
        <div className="text-[12px] text-white/30 max-w-[240px]">
          Switch to "Find people" and send a request.
        </div>
      </div>
    );
  }
  if (filtered.length === 0) {
    return (
      <div className="py-12 px-6 text-center text-white/30 text-[12px]">
        No friend matches "{query}".
      </div>
    );
  }

  return (
    <ul className="divide-y divide-white/5">
      {filtered.map((f) => (
        <li
          key={f.address}
          className="px-4 py-3 flex items-center gap-3 hover:bg-white/3 transition-colors"
        >
          <Avatar
            address={f.address}
            handle={f.handle}
            src={avatarUrl(f.address, f.avatarVersion)}
            size={36}
            className="ring-1 ring-white/10"
          />
          <div className="flex-1 min-w-0">
            <div className="text-[13px] text-white font-semibold truncate">{f.handle}</div>
            <div className="font-mono text-[10px] text-white/40">{shortAddr(f.address)}</div>
          </div>
          {onChallenge && (
            <button
              onClick={() =>
                onChallenge({ address: f.address, handle: f.handle, avatarVersion: f.avatarVersion })
              }
              aria-label={`Challenge ${f.handle} to a match`}
              className="p-1.5 rounded-md hover:bg-white/5 text-white/40 hover:text-[#FF6B00] transition-colors"
              title="Challenge to a match"
            >
              <Swords className="w-4 h-4" />
            </button>
          )}
          {onViewProfile && (
            <button
              onClick={() => onViewProfile(f.address, f.handle)}
              aria-label={`View ${f.handle}'s profile`}
              className="p-1.5 rounded-md hover:bg-white/5 text-white/40 hover:text-[#FF6B00] transition-colors"
              title="View profile"
            >
              <UserRound className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => friendsStore.removeFriend(f.address)}
            aria-label={`Remove ${f.handle}`}
            className="p-1.5 rounded-md hover:bg-white/5 text-white/40 hover:text-red-400 transition-colors"
            title="Remove friend"
          >
            <UserMinus className="w-4 h-4" />
          </button>
        </li>
      ))}
    </ul>
  );
}

function FindList({
  query,
  results,
  searching,
  error,
  friendSet,
  outgoingSet,
  disabled,
  onAdd,
  onViewProfile,
}: {
  query: string;
  results: DiscoverableUser[];
  searching: boolean;
  error: string | null;
  friendSet: Set<string>;
  outgoingSet: Set<string>;
  disabled: boolean;
  onAdd: (u: DiscoverableUser) => void;
  onViewProfile?: (address: string, handle: string) => void;
}) {
  if (results.length === 0) {
    return (
      <div className="py-12 px-6 text-center text-white/30 text-[12px]">
        {searching ? (
          <>Searching…</>
        ) : query ? (
          `No players match "${query}".`
        ) : (
          "No discoverable players yet."
        )}
        {error && (
          <div className="mt-2 text-[11px] text-amber-500/70">
            Showing fallback list · {error}
          </div>
        )}
      </div>
    );
  }
  return (
    <ul className="divide-y divide-white/5">
      {results.map((u) => {
        const lower = u.address.toLowerCase();
        const already = friendSet.has(lower);
        const pending = outgoingSet.has(lower);
        return (
          <li
            key={u.address}
            className="px-4 py-3 flex items-center gap-3 hover:bg-white/3 transition-colors"
          >
            <span className="relative shrink-0">
              <Avatar
                address={u.address}
                handle={u.handle}
                src={avatarUrl(u.address, u.avatarVersion)}
                size={36}
                className="ring-1 ring-white/10"
              />
              {u.online && (
                <span
                  className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-[#111] bg-emerald-400"
                  aria-label="online"
                />
              )}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] text-white font-semibold truncate flex items-center gap-1.5">
                {u.handle}
                {u.online && (
                  <span className="text-[9px] text-emerald-400 font-mono tracking-[0.14em] uppercase">
                    online
                  </span>
                )}
              </div>
              <div className="font-mono text-[10px] text-white/40">{shortAddr(u.address)}</div>
            </div>
            {onViewProfile && (
              <button
                onClick={() => onViewProfile(u.address, u.handle)}
                aria-label={`View ${u.handle}'s profile`}
                className="p-1.5 rounded-md hover:bg-white/5 text-white/40 hover:text-[#FF6B00] transition-colors"
                title="View profile"
              >
                <UserRound className="w-4 h-4" />
              </button>
            )}
            {already ? (
              <span className="px-2.5 py-1.5 rounded-md font-mono text-[10px] tracking-[0.14em] uppercase text-emerald-400 border border-emerald-400/30 inline-flex items-center gap-1.5">
                <Check className="w-3 h-3" /> Friend
              </span>
            ) : pending ? (
              <span className="px-2.5 py-1.5 rounded-md font-mono text-[10px] tracking-[0.14em] uppercase text-[#FF6B00] border border-[#FF6B00]/30 inline-flex items-center gap-1.5">
                <Clock className="w-3 h-3" /> Pending
              </span>
            ) : (
              <button
                onClick={() => onAdd(u)}
                disabled={disabled}
                className="px-3 py-1.5 rounded-md bg-[#FF6B00] hover:bg-[#FF8542] disabled:opacity-30 disabled:cursor-not-allowed text-black font-mono text-[10px] tracking-[0.14em] uppercase font-bold inline-flex items-center gap-1.5 transition-colors"
              >
                <UserPlus className="w-3 h-3" /> Add friend
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}
